import { CustomBanner } from '@/components';
import Layout from '@/components/Layout';
import { EmailProvider } from '@/constants/email';
import { useDisconnectEmailMutation, useGetNotificationAccountsQuery } from '@/redux/api/api.caller';
import { haveClosedBannerSelector } from '@/redux/features/dashboard.slice';
import emailSlice, { showNewVersionSelector } from '@/redux/features/email.slice';
import { Card, SkeletonBodyText } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SenderInfo from '../SenderInfo';
import EmailConnectProvider from '../EmailConnectProvider';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import commonSlice from '@/redux/features/common.slice';
import SwitchToNewVersion from '../SwitchToNewVersion';
import { PATH } from '@/constants/path';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';

const EmailSender = () => {
  const dispatch = useDispatch();
  const navigate = useConfirmationNavigate();
  const [apiError, setApiError] = useState<string>('');
  const isShowNewVersion = useSelector(showNewVersionSelector);
  const haveClosedBanner = useSelector(haveClosedBannerSelector);

  const [disconnectEmail, disconnectEmailData] = useDisconnectEmailMutation();
  const { data, isLoading } = useGetNotificationAccountsQuery();
  const emailAccount = data?.data?.email?.account;

  const connectBusinessEmail = useMemo(() => {
    if (isLoading) {
      return (
        <Card>
          <div style={{ height: 300 }}>
            <SkeletonBodyText lines={15} />
          </div>
        </Card>
      );
    }
    if (!emailAccount || emailAccount.gateway === EmailProvider['Amazon SES']) {
      return <SenderInfo />;
    }
    return <EmailConnectProvider />;
  }, [emailAccount, isLoading]);

  const handleKeepCurrentVersion = () => {
    dispatch(emailSlice.actions.handleShowNewVersion(false));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePreviewNewVersion = () => dispatch(emailSlice.actions.handleShowNewVersion(true));

  const handleSwitchNewVersion = () => {
    disconnectEmail({}).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        dispatch(emailSlice.actions.handleShowNewVersion(false));
        dispatch(emailSlice.actions.handleOpenNewVersionModal(false));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (apiError) {
          setApiError('');
        }
        return;
      }
      setApiError(getErrorFromAPI(res));
    });
  };

  const handleCloseBanner = () => {
    dispatch(emailSlice.actions.handleCloseBanner(true));
  };

  return (
    <Layout
      backAction={{ onAction: () => navigate(PATH.NOTIFICATIONS) }}
      title="Sender email"
      subtitle="Set the email address used to send messages to customers"
    >
      {emailAccount && emailAccount.gateway !== EmailProvider['Amazon SES'] && (
        <CustomBanner
          isClose={haveClosedBanner}
          maxHeight="13.5rem"
          title="New email version available"
          secondaryAction={{ content: 'Preview new version', onAction: handlePreviewNewVersion }}
          tone="info"
          onDismiss={handleCloseBanner}
        >
          We have released the new version of the email notification feature. You can decide whether to keep the current version
          or switch to the new one by previewing with the button below
        </CustomBanner>
      )}
      <SwitchToNewVersion
        isOpen={isShowNewVersion}
        onKeepCurrentVersion={handleKeepCurrentVersion}
        onSwitchNewVersion={handleSwitchNewVersion}
        isLoading={disconnectEmailData.isLoading}
        error={apiError}
      />
      {connectBusinessEmail}
    </Layout>
  );
};

export default EmailSender;
